import { ValueErrorType, type ErrorFunction } from '@sinclair/typebox/errors';
import i18n from './i18n.js';
import { isBlank } from './zod-error-map.js';

/**
 * TypeBox's counterpart of `zodErrorMap`, for the forms validated against a
 * contract schema through `typeboxResolver`: the same `validation.*` messages, in
 * the language the operator picked, instead of TypeBox's English ("Expected string
 * length greater or equal to 2").
 */
export const typeboxErrorFunction: ErrorFunction = ({ errorType, schema, value }) => {
  const t = (key: string, vars?: Record<string, unknown>) => i18n.t(key, vars);
  const n = (bound: unknown) => ({ n: String(bound) });
  if (isBlank(value)) return t('validation.required');

  switch (errorType) {
    case ValueErrorType.StringMinLength:
      return t('validation.lengthMin', n(schema.minLength));
    case ValueErrorType.StringMaxLength:
      return t('validation.lengthMax', n(schema.maxLength));
    case ValueErrorType.StringFormat:
      if (schema.format === 'email') return t('validation.email');
      if (schema.format === 'uri' || schema.format === 'url') return t('validation.url');
      return t('validation.format');
    case ValueErrorType.StringFormatUnknown:
    case ValueErrorType.StringPattern:
      return t('validation.format');
    case ValueErrorType.NumberMinimum:
    case ValueErrorType.IntegerMinimum:
      return t('validation.numberMin', n(schema.minimum));
    case ValueErrorType.NumberExclusiveMinimum:
    case ValueErrorType.IntegerExclusiveMinimum:
      return t('validation.numberAbove', n(schema.exclusiveMinimum));
    case ValueErrorType.NumberMaximum:
    case ValueErrorType.IntegerMaximum:
      return t('validation.numberMax', n(schema.maximum));
    case ValueErrorType.NumberExclusiveMaximum:
    case ValueErrorType.IntegerExclusiveMaximum:
      return t('validation.numberBelow', n(schema.exclusiveMaximum));
    case ValueErrorType.NumberMultipleOf:
    case ValueErrorType.IntegerMultipleOf:
      return t('validation.multipleOf', n(schema.multipleOf));
    case ValueErrorType.Number:
      return t('validation.number');
    case ValueErrorType.Integer:
      return t('validation.integer');
    case ValueErrorType.ArrayMinItems:
      return t('validation.pickMin', n(schema.minItems));
    case ValueErrorType.ArrayMaxItems:
      return t('validation.pickMax', n(schema.maxItems));
    case ValueErrorType.Date:
      return t('validation.date');
    case ValueErrorType.Union:
    case ValueErrorType.Literal:
      return t('validation.choice');
    default:
      return t('validation.invalid');
  }
};
